import { z } from 'zod';

/*
 * Usuários do Lastrouter (ADR 0045). Os papéis moram no Entra, como app roles do registro do app
 * (Lastrouter.Leitor, Lastrouter.Editor, Lastrouter.Admin). Esta tela lê e muda as atribuições pelo
 * Microsoft Graph; o Lastrouter não guarda lista própria de quem tem qual papel.
 */

/** Os três papéis do ADR 0036. Os nomes no Entra vêm da configuração (access.reader_role, ...). */
export const UserRoleSchema = z.enum(['reader', 'editor', 'admin']);
export type UserRole = z.infer<typeof UserRoleSchema>;

/**
 * Ligação com o Graph: ok; not_configured (sem login do painel no Entra); forbidden (o Graph
 * respondeu 401/403, faltam as permissões de aplicativo); error (qualquer outra falha).
 */
export const GraphStatusSchema = z.object({
  state: z.enum(['ok', 'not_configured', 'not_connected', 'forbidden', 'error']),
  message: z.string().nullable(),
});
export type GraphStatus = z.infer<typeof GraphStatusSchema>;

export const UserRowSchema = z.object({
  /** Id do objeto no Entra (usuário ou grupo). Nulo para quem só apareceu na auditoria. */
  id: z.string().nullable(),
  kind: z.enum(['user', 'group']),
  name: z.string().nullable(),
  /** Login como o Lastrouter vê a pessoa (UPN ou e-mail, pela configuração). Nulo em grupo. */
  login: z.string().nullable(),
  roles: z.array(UserRoleSchema),
  /** Usuário em cada fonte, pela mesma regra de mapeamento que a fonte usa na consulta. */
  source_users: z.array(z.object({ source_id: z.string(), user: z.string().nullable(), detail: z.string() })),
  /** Última linha de auditoria ou conversa desta pessoa (90 dias). */
  last_seen_at: z.string().nullable(),
  /** entra: atribuído no registro do app. audit: só apareceu na auditoria. */
  origin: z.enum(['entra', 'audit']),
});
export type UserRow = z.infer<typeof UserRowSchema>;

export const UserListSchema = z.object({
  graph: GraphStatusSchema,
  users: z.array(UserRowSchema),
  /** Id de quem está logado: a tela não oferece tirar o próprio Admin. */
  self_id: z.string(),
});
export type UserList = z.infer<typeof UserListSchema>;

export const DirectoryUserSchema = z.object({ id: z.string(), name: z.string().nullable(), login: z.string().nullable() });
export type DirectoryUser = z.infer<typeof DirectoryUserSchema>;

export const USERS_ROUTES = {
  users: { method: 'GET', path: '/users', access: 'admin', summary: 'Pessoas com papel no Entra e quem já usou o Lastrouter', response: UserListSchema },
  searchDirectory: {
    method: 'GET',
    path: '/users/directory',
    access: 'admin',
    summary: 'Busca pessoas no diretório do Entra por nome ou e-mail',
    query: z.object({ q: z.string().trim().min(2).max(100) }),
    response: z.array(DirectoryUserSchema),
  },
  setUserRole: {
    method: 'PUT',
    path: '/users/:id/role',
    access: 'admin',
    summary: 'Muda o papel de uma pessoa no Entra (nulo tira o acesso)',
    params: z.object({ id: z.string().regex(/^[\w:.@-]{1,128}$/) }),
    body: z.object({ role: UserRoleSchema.nullable() }),
    response: z.object({ id: z.string(), roles: z.array(UserRoleSchema) }),
  },
} as const;
